import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'

import Button from '@doordash/component-button'
import { Modal } from '@doordash/component-modal'
import { Text, Spacing, StackChildren } from '@doordash/design-language'
import { has, noop } from 'lodash'
import { Error } from './styles'

import { EmailField, PasswordField } from '@doordash/component-fields'
import { validators as sharedValidators } from '../../../utilities/validators'

import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import Form from '../../../utilities/Form'

import t from './strings'

type Props = {
  uniqueLink: string
  currentStep?: string
  email?: string
  message?: string
  login?: (email: string, password: string, uniqueLink: string) => Promise<any>
  trackForgotPassword?: () => Promise<any>
  trackDasherLogin?: (data: DA.DasherLogin) => Promise<any>
}

export class LoginModal extends React.Component<Props> {
  public static fields = {
    email: '',
    password: '',
  }

  public static validators = {
    email: sharedValidators.email,
    password: sharedValidators.password,
  }

  public static defaultProps = {
    message: t.useExistingCredentials(),
    login: noop,
    trackForgotPassword: noop,
    trackDasherLogin: noop,
    currentStep: noop,
  }

  public state = {
    error: '',
  }

  public componentDidMount() {
    const { trackDasherLogin, currentStep } = this.props
    trackDasherLogin({
      currentStep,
      action: DA.EventActions.Rendered,
    })
  }

  public onForgotPassword = async e => {
    e.preventDefault()
    await this.props.trackForgotPassword()
    location.href = 'https://www.doordash.com/accounts/password/reset/'
  }

  public submitHandler = async data => {
    const { uniqueLink, login, trackDasherLogin } = this.props
    const { email, password } = data

    trackDasherLogin({
      action: DA.EventActions.Submit,
      name: 'In Progress',
    })

    try {
      this.setState({ error: '' })
      await login(email, password, uniqueLink)

      trackDasherLogin({
        action: DA.EventActions.Submit,
        name: 'Success',
      })

      window.location.reload(true)
    } catch (error) {
      let errorMessage = ''

      if (has(error, 'response.data.non_field_errors')) {
        errorMessage = error.response.data.non_field_errors
        this.setState({ error: error.response.data.non_field_errors })
      }
      if (has(error, 'message')) {
        errorMessage = error.message
        this.setState({ error: error.message })
      }
      if (typeof error === 'string') {
        errorMessage = error
        this.setState({ error })
      }
      if (has(error, 'non_field_errors')) {
        errorMessage = error.non_field_errors
        this.setState({ error: error.non_field_errors })
      }

      trackDasherLogin({
        action: DA.EventActions.Error,
        name: 'Error Message',
        value: errorMessage,
      })
    }
  }

  public render() {
    const { error } = this.state
    const { message, email } = this.props
    if (email !== null) {
      LoginModal.fields.email = email
    }

    return (
      <Modal title={t.title()} hasCloseButton={false}>
        <StackChildren>
          <Text>{message}</Text>
          <Form
            onSubmit={this.submitHandler}
            validators={LoginModal.validators}
            fields={LoginModal.fields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form onSubmit={handleSubmit}>
                <StackChildren size={Spacing.XLarge}>
                  <EmailField
                    label={t.email()}
                    placeholder={email || t.placeholderEmail()}
                    onChange={handleChange.bind(this, 'email')}
                    autoComplete="email"
                    description={t.required()}
                    isDisabled={email}
                    {...fields.email}
                  />
                  <PasswordField
                    label={t.password()}
                    onChange={handleChange.bind(this, 'password')}
                    description={t.required()}
                    autoComplete="current-password"
                    {...fields.password}
                  />
                </StackChildren>
                <br />
                <StackChildren size={Spacing.XLarge}>
                  <Button
                    onClick={this.onForgotPassword}
                    type={Button.Types.FlatPrimary}
                    tag="a"
                  >
                    {t.forgotYourPassword()}
                  </Button>
                  {error && (
                    <Error isSquished>
                      <Text size={Text.Sizes.S12}>{error}</Text>
                    </Error>
                  )}
                  <Button
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    onClick={async () => {
                      try {
                        await handleSubmit()
                      } catch (e) {
                        console.warn(e)
                      }
                    }}
                  >
                    {t.signIn()}
                  </Button>
                </StackChildren>
              </form>
            )}
          </Form>
        </StackChildren>
      </Modal>
    )
  }
}

const mapDispatchToProps = {
  login: actions.login,
  trackDasherLogin: events.trackDasherLogin,
  trackForgotPassword: events.trackForgotPassword,
}

export default connect(null, mapDispatchToProps)(LoginModal)
